import {
  AlertCircle,
  AlertTriangle,
  ArrowLeftRight,
  BarChart3,
  CalendarDays,
  Check,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  CircleCheck,
  Clock,
  Columns3,
  DollarSign,
  Download,
  FileWarning,
  HelpCircle,
  ImageIcon,
  Info,
  LineChart,
  Mail,
  MapPin,
  Menu,
  Minus,
  MoreVertical,
  Pencil,
  Pin,
  Plus,
  RefreshCw,
  Search,
  Send,
  Trash2,
  Upload,
  UserCheck,
  X,
} from "lucide-react";

/**
 * PunchupIcons — the curated PURPOSE → lucide icon registry for the Punchup
 * token-CSS tier.
 *
 * WHY this exists: tixologist shipped a 120-file `/public/icons/*.svg` pool keyed
 * by an opaque string `iconName` (`dollarSignWhite`, `blueCalendar`, `hamburger`,
 * `trashGrey`…). That pool encoded COLOUR + theme into the filename, so the same
 * concept had three or four variants and the meaning was lost. The downstroke tier
 * draws every icon from `lucide-react` instead (one icon source, themed by
 * `currentColor` / token CSS — see `punchup/CLAUDE.md` invariant 1). This registry
 * is the curated **purpose-first** index over that single source: you pick by what
 * the icon MEANS ("the refresh action", "the location of a venue"), not by a lucide
 * component name. It is the runtime companion to the `purpose → icon` guide and the
 * legacy-SVG migration map in `packages/styles/docs/icons.md`.
 *
 * NOT A HARD GATE. `lucide-react` stays directly importable for one-off or
 * not-yet-curated glyphs (e.g. the chart-type legend icons `LineChart` /
 * `BarChart3`, or a brand-new concept). Reach for `PunchupIcons` first so shared
 * purposes resolve to ONE glyph product-wide; fall back to a direct lucide import
 * when no curated purpose fits, and add the purpose here once a second caller wants
 * the same concept (DRY emerges on the second pass).
 *
 * Curation rule: a key is a PURPOSE (`refresh`, `add`, `location`), never a colour
 * or a lucide name. The value is the lucide component. Keep the set tight — every
 * entry is a purpose at least one tier component reaches for, or a migration target
 * named in `docs/icons.md`. Sizes come from `PUNCHUP_ICON_SIZE`.
 */
export const PunchupIcons = {
  // — Actions —
  add: Plus,
  remove: Minus,
  refresh: RefreshCw,
  download: Download,
  upload: Upload,
  send: Send,
  delete: Trash2,
  edit: Pencil,
  clear: X,
  swap: ArrowLeftRight,

  // — Navigation / disclosure —
  menu: MoreVertical,
  hamburger: Menu,
  expand: ChevronDown,
  collapse: ChevronUp,
  back: ChevronLeft,
  forward: ChevronRight,

  // — Data / table affordances —
  columns: Columns3,
  pin: Pin,
  search: Search,

  // — Domain concepts —
  date: CalendarDays,
  time: Clock,
  location: MapPin,
  money: DollarSign,
  mail: Mail,
  attendee: UserCheck,
  image: ImageIcon,

  // — Status / feedback —
  help: HelpCircle,
  alert: AlertCircle,
  warning: AlertTriangle,
  info: Info,
  success: CircleCheck,
  fileWarning: FileWarning,
  check: Check,
} as const;

/** Union of curated purpose keys — `"add" | "refresh" | "delete" | …`. */
export type PunchupIconName = keyof typeof PunchupIcons;

/**
 * The three sanctioned icon sizes for the tier, in px. `sm` (16) is the
 * in-button / inline glyph (matches the 16px lucide size every `PunchupButton`
 * icon wrapper already passes), `md` (20) the default standalone glyph, `lg` (24)
 * the emphasis / tile-header glyph. Pass as `size={PUNCHUP_ICON_SIZE.sm}`; do not
 * inline a raw pixel number so the scale stays in one place.
 */
export const PUNCHUP_ICON_SIZE = { sm: 16, md: 20, lg: 24 } as const;
